import type { SearchResultItem } from "@/client"
import { HtmlContent } from "@/components/Common/HtmlContent"
import { QuizQuestionSaveNoteModal } from "@/components/TestTaking/QuizQuestionSaveNoteModal"
import { Tooltip } from "@/components/ui/tooltip"
import { hasHtmlContent, stripOptionPrefix } from "@/utils/text-format"
import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  Span,
  Stack,
  Text,
} from "@chakra-ui/react"
import { Link } from "@tanstack/react-router"
import { useMemo, useState } from "react"
import { FaRegBookmark } from "react-icons/fa"
import { MyAnswersPanel } from "./MyAnswersPanel"

/** Why the bookmark control is disabled, in the voice of the locked rationale. */
const BOOKMARK_LOCKED_MESSAGE =
  "You can bookmark this once you've answered it in a quiz."

interface QuestionResultCardProps {
  item: SearchResultItem
}

export function QuestionResultCard({ item }: QuestionResultCardProps) {
  const [showChoices, setShowChoices] = useState(false)
  const [showRationale, setShowRationale] = useState(false)
  const [showMyAnswers, setShowMyAnswers] = useState(false)

  const options = item.options ?? []
  // The server omits correct_answer along with a locked rationale. Never infer
  // it: with no value there is simply no correct-answer marker to draw.
  const correctAnswer =
    item.correct_answer === null || item.correct_answer === undefined
      ? null
      : item.correct_answer
  const rationaleUnlocked = item.rationale_unlocked ?? false
  // Unlocked but blank (null, or editor leftovers like "<p></p>") means the
  // question has no rationale, so there is nothing for the button to reveal.
  const hasRationale = useMemo(
    () => rationaleUnlocked && hasHtmlContent(item.rationale),
    [rationaleUnlocked, item.rationale],
  )
  const answerCount = item.answer_count ?? 0
  const topicNames = item.topic_names ?? []
  const subjectNames = item.subject_names ?? []
  // Where the question lives. It leads the header so a result reads the same
  // way a flashcard's deck badge does.
  const quizNames = item.quiz_names ?? []

  return (
    <Box borderWidth="1px" borderRadius="lg" bg="bg.surface" p={4}>
      <Stack gap={3}>
        {quizNames.length > 0 ||
        topicNames.length > 0 ||
        subjectNames.length > 0 ? (
          <Flex gap={2} wrap="wrap">
            {quizNames.map((quiz) => (
              <Badge key={`quiz-${quiz}`} colorPalette="blue" variant="subtle">
                {quiz}
              </Badge>
            ))}
            {subjectNames.map((subject) => (
              <Badge
                key={`subject-${subject}`}
                colorPalette="teal"
                variant="subtle"
              >
                {subject}
              </Badge>
            ))}
            {topicNames.map((topic) => (
              <Badge
                key={`topic-${topic}`}
                colorPalette="purple"
                variant="subtle"
              >
                {topic}
              </Badge>
            ))}
          </Flex>
        ) : null}

        <Heading size="sm" lineHeight="1.35">
          <HtmlContent content={item.question_text} />
        </Heading>

        <Flex gap={2} wrap="wrap" align="center">
          <Button
            size="xs"
            variant="outline"
            onClick={() => setShowChoices((open) => !open)}
          >
            {showChoices ? "Hide choices" : "Show choices"}
          </Button>
          {hasRationale && (
            <Button
              size="xs"
              variant="outline"
              onClick={() => setShowRationale((open) => !open)}
            >
              {showRationale ? "Hide rationale" : "Show rationale"}
            </Button>
          )}
          {answerCount > 0 && (
            <Button
              size="xs"
              variant="outline"
              onClick={() => setShowMyAnswers((open) => !open)}
            >
              {showMyAnswers
                ? `Hide my answers (${answerCount})`
                : `My answers (${answerCount})`}
            </Button>
          )}
          {renderBookmarkControl(item)}
        </Flex>

        {showChoices && (
          <Stack gap={2}>
            {options.map((option, optionIndex) => {
              const isCorrect =
                correctAnswer !== null && optionIndex === correctAnswer
              return (
                <Flex
                  key={optionIndex}
                  align="center"
                  justify="space-between"
                  gap={3}
                  borderWidth="1px"
                  borderColor={isCorrect ? "green.500" : "border"}
                  borderRadius="md"
                  bg={isCorrect ? "green.subtle" : "bg.subtle"}
                  p={3}
                >
                  <Text>
                    {String.fromCharCode(65 + optionIndex)}.{" "}
                    {stripOptionPrefix(option)}
                  </Text>
                  {isCorrect && (
                    <Badge colorPalette="green" flexShrink={0}>
                      Correct answer
                    </Badge>
                  )}
                </Flex>
              )
            })}
          </Stack>
        )}

        {rationaleUnlocked
          ? hasRationale &&
            showRationale && (
              <Box
                bg="blue.subtle"
                borderColor="blue.300"
                borderWidth="1px"
                borderRadius="md"
                p={3}
              >
                <Text
                  fontSize="xs"
                  fontWeight="semibold"
                  color="blue.fg"
                  mb={1}
                >
                  Rationale
                </Text>
                <HtmlContent content={item.rationale} color="fg.muted" />
              </Box>
            )
          : renderLockedRationale(item)}

        <MyAnswersPanel
          questionId={item.id}
          isOpen={showMyAnswers}
          options={item.options}
        />
      </Stack>
    </Box>
  )
}

/**
 * The bookmark control, enabled only when the server named a quiz for it.
 *
 * QuizQuestionUserNote is keyed on (org, user, quiz, question), so a bookmark
 * needs a quiz and a search result is only a question. The server sets
 * bookmark_quiz_id to a quiz the caller answered this question in that would
 * still accept the write; when it is null there is no quiz to write under, so
 * the button is genuinely disabled rather than left to 404 on click.
 */
function renderBookmarkControl(item: SearchResultItem) {
  if (item.bookmark_quiz_id) {
    return (
      <QuizQuestionSaveNoteModal
        quizId={item.bookmark_quiz_id}
        question={{
          question_id: item.id,
          question_text: item.question_text ?? "",
        }}
      />
    )
  }

  return (
    <Tooltip content={BOOKMARK_LOCKED_MESSAGE}>
      {/* A disabled button fires no pointer events, so the tooltip hangs off
          a wrapper that does. */}
      <Span display="inline-flex">
        {/* Styled exactly like QuizQuestionSaveNoteModal's trigger so the two
            states of this control are the same shape on every card. */}
        <Button
          aria-label="Save note"
          size="sm"
          variant="ghost"
          gap={{ base: 0, md: 2 }}
          px={{ base: 2, md: 4 }}
          disabled
        >
          <FaRegBookmark />
          <Box as="span" display={{ base: "none", md: "inline" }}>
            Save note
          </Box>
        </Button>
      </Span>
    </Tooltip>
  )
}

function renderLockedRationale(item: SearchResultItem) {
  if (item.unlock_quiz_id) {
    return (
      <Text fontSize="sm" color="fg.muted">
        <Link to="/quizzes/$quizId" params={{ quizId: item.unlock_quiz_id }}>
          Attempt {item.unlock_quiz_name ?? "the quiz"} to unlock the rationale
        </Link>
      </Text>
    )
  }

  return (
    <Text fontSize="sm" color="fg.muted">
      The rationale for this question is not available to you.
    </Text>
  )
}
